import { EnhancedFormikForm } from '@/components/EnhancedFormikForm';
import { getStateOptions } from '@/helpers/getStateOptions';
import { isErrorWithData } from '@/services/helpers';
import {
  Badge,
  Box,
  Button,
  FormikSelectInput,
  FormikSelectInputNative,
  FormikTextInput,
  SelectInputNative,
  Spinner,
  toast,
} from '@palmetto/palmetto-components';
import { Field, FieldArray, Formik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import {
  DirectPayMaterialVendorAliasOptions,
  OrganizationDirectPaySettingsV2,
  ProgramType,
  ProgramTypeLabels,
} from 'types';

import {
  useGetDirectPayMaterialVendorsQuery,
  useGetOrganizationDirectPaySettingsQuery,
  useGetSplitPayProgramNamesQuery,
  usePutOrganizationDirectPaySettingsMutation,
} from '../../../../services/organizations';
import { OrganizationNode } from '../../../../types/Organizations';
import FormSection from '../../../Forms/FormSection';

interface DirectPayMaterialVendor {
  directPayMaterialVendorAlias: string;
  directPayMaterialVendorName: string;
  subCompanies?: Array<{ name: string; id: string }>;
}

interface DirectPayProps {
  organization: OrganizationNode;
  isLoading?: boolean;
}

interface FormStateOverrideItem {
  directPayMaterialVendorAlias: string;
  directPayInstallerIdentifier: string;
  programName: string;
  subCompanyId: string;
  states: Array<{ label: string; value: string }>;
}

interface FormOptInVendorItem {
  directPayMaterialVendorAlias: string;
  directPayInstallerIdentifier: string;
  programName: string;
  subCompanyId: string;
}

const getUsedVendorSubCompanyPairs = (stateOverrides: FormStateOverrideItem[], excludeIndex?: number): Set<string> =>
  new Set(
    stateOverrides
      .filter((_, i) => i !== excludeIndex)
      .map((o) => `${o.directPayMaterialVendorAlias}:${o.subCompanyId}`),
  );

const directPaySchema = yup.object().shape({
  directPayDefaultEnabled: yup.boolean(),
  directPayMaterialVendorAlias: yup.string(),
  directPayInstallerIdentifier: yup.string(),
  subCompanyId: yup.string(),
  stateOverrides: yup.array().of(
    yup.object().shape({
      directPayMaterialVendorAlias: yup.string().required('Material vendor is required'),
      directPayInstallerIdentifier: yup.string(),
      subCompanyId: yup.string(),
      states: yup.array().min(1, 'At least one state must be selected'),
    }),
  ),
  additionalOptInVendors: yup.array().of(
    yup.object().shape({
      directPayMaterialVendorAlias: yup.string().required('Material vendor is required'),
      directPayInstallerIdentifier: yup.string(),
      subCompanyId: yup.string(),
    }),
  ),
});

const DirectPay = ({ organization }: DirectPayProps) => {
  const navigate = useNavigate();
  const [programType, setProgramType] = useState<string>(organization?.programTypes?.[0] as string);
  const [updateDirectPaySettingsMutation] = usePutOrganizationDirectPaySettingsMutation();
  const { data: directPayProgramNames } = useGetSplitPayProgramNamesQuery();
  const { data, isLoading, isFetching } = useGetOrganizationDirectPaySettingsQuery(
    { alias: organization.alias, programType: programType as ProgramType },
    { skip: !organization.alias || !programType },
  );
  const statesData = getStateOptions();

  const directPayData = data?.data?.[0];

  const { data: directPayMaterialVendors, isLoading: areDirectPayMaterialVendorsLoading } =
    useGetDirectPayMaterialVendorsQuery();

  const formattedDirectPayMaterialVendors = useMemo(() => {
    if (!directPayMaterialVendors?.materialVendors) return [];

    return directPayMaterialVendors.materialVendors.map((vendor: DirectPayMaterialVendor) => ({
      label: vendor.directPayMaterialVendorName,
      value: vendor.directPayMaterialVendorAlias,
      subCompanies: vendor.subCompanies || [],
    }));
  }, [directPayMaterialVendors]);

  const getSubCompanyOptions = useCallback(
    (vendorAlias: string) => {
      if (!directPayMaterialVendors?.materialVendors) return [];

      const vendor = directPayMaterialVendors.materialVendors.find(
        (v: DirectPayMaterialVendor) => v.directPayMaterialVendorAlias === vendorAlias,
      );

      if (!vendor || !vendor.subCompanies || !vendor.subCompanies.length) return [];

      return vendor.subCompanies.map((subCompany) => ({
        label: subCompany.name,
        value: subCompany.id,
      }));
    },
    [directPayMaterialVendors],
  );

  const programNameOptions = useMemo(() => {
    if (!directPayProgramNames) {
      return [];
    }

    return directPayProgramNames.programs.map((program) => ({
      label: program.programName,
      value: program.programId,
    }));
  }, [directPayProgramNames]);

  const stateOptions = useMemo(() => {
    if (!statesData) return [];

    return statesData.map((state: any) => ({
      label: state.label,
      value: state.value,
    }));
  }, [statesData]);

  const getInitialValues = (settings: OrganizationDirectPaySettingsV2) => {
    const {
      stateOverrides: backendStateOverrides = [],
      additionalOptInVendors: backendOptInVendors = [],
      ...rest
    } = settings;
    const formStateOverrides: FormStateOverrideItem[] = [];
    const formOptInVendors: FormOptInVendorItem[] = [];

    const stateMap = stateOptions.reduce((acc: Record<string, any>, state: any) => {
      acc[state.value] = state;
      return acc;
    }, {});

    backendStateOverrides.forEach((override: any) => {
      if (override && override.states && Array.isArray(override.states)) {
        formStateOverrides.push({
          directPayMaterialVendorAlias: override.directPayMaterialVendorAlias,
          directPayInstallerIdentifier: override.directPayInstallerIdentifier,
          programName: override.programName || '',
          subCompanyId: override.subCompanyId || '',
          states: override.states.map(
            (stateAbbr: string) => stateMap[stateAbbr] || { label: stateAbbr, value: stateAbbr },
          ),
        });
      }
    });

    backendOptInVendors.forEach((vendor: any) => {
      if (vendor) {
        formOptInVendors.push({
          directPayMaterialVendorAlias: vendor.directPayMaterialVendorAlias,
          directPayInstallerIdentifier: vendor.directPayInstallerIdentifier,
          programName: vendor.programName || '',
          subCompanyId: vendor.subCompanyId || '',
        });
      }
    });

    const initialValues = {
      ...rest,
      stateOverrides: formStateOverrides,
      additionalOptInVendors: formOptInVendors,
    } as Record<string, any>;

    return initialValues;
  };

  const validateVendorSettings = (
    vendor: {
      directPayMaterialVendorAlias?: string;
      directPayInstallerIdentifier?: string;
      programName?: string;
      subCompanyId?: string;
    },
    isDirectPayEnabled: boolean,
  ) => {
    const vendorErrors: any = {};

    if (!vendor.directPayMaterialVendorAlias) {
      vendorErrors.directPayMaterialVendorAlias = 'Material vendor is required';
    }

    if (isDirectPayEnabled && !vendor.directPayInstallerIdentifier) {
      vendorErrors.directPayInstallerIdentifier = 'The Direct Pay Installer ID is required';
    }

    if (vendor.directPayMaterialVendorAlias === DirectPayMaterialVendorAliasOptions.ced && !vendor.programName) {
      vendorErrors.programName = 'Program name is required';
    }

    const selectedVendor = directPayMaterialVendors?.materialVendors?.find(
      (v: DirectPayMaterialVendor) => v.directPayMaterialVendorAlias === vendor.directPayMaterialVendorAlias,
    );

    if (
      selectedVendor &&
      selectedVendor.subCompanies &&
      selectedVendor.subCompanies.length > 0 &&
      !vendor.subCompanyId
    ) {
      vendorErrors.subCompanyId = 'Sub-company selection is required for this vendor';
    }

    return vendorErrors;
  };

  const handleValidation = (values: any) => {
    const errors = {} as any;

    if (values.directPayDefaultEnabled) {
      if (!values.directPayInstallerIdentifier) {
        errors.directPayInstallerIdentifier = 'The Direct Pay ID of the EPC is required when Direct Pay is enabled';
      }
      const selectedVendor = directPayMaterialVendors?.materialVendors?.find(
        (vendor: DirectPayMaterialVendor) =>
          vendor.directPayMaterialVendorAlias === values.directPayMaterialVendorAlias,
      );

      if (
        selectedVendor &&
        selectedVendor.subCompanies &&
        selectedVendor.subCompanies.length > 0 &&
        !values.subCompanyId
      ) {
        errors.subCompanyId = 'Sub-company selection is required for this vendor';
      }
    }

    if (values.additionalOptInVendors && values.additionalOptInVendors.length > 0) {
      const optInVendorErrors: any[] = [];

      values.additionalOptInVendors.forEach((vendor: FormOptInVendorItem, index: number) => {
        const vendorErrors = validateVendorSettings(vendor, true);

        if (Object.keys(vendorErrors).length > 0) {
          optInVendorErrors[index] = vendorErrors;
        }
      });

      if (optInVendorErrors.length > 0) {
        errors.additionalOptInVendors = optInVendorErrors;
      }
    }

    if (values.stateOverrides && values.stateOverrides.length > 0) {
      const stateOverrideErrors: any[] = [];

      values.stateOverrides.forEach((override: FormStateOverrideItem, index: number) => {
        const overrideErrors = validateVendorSettings(override, true);

        if (!override.states || override.states.length === 0) {
          overrideErrors.states = 'At least one state must be selected';
        }

        if (Object.keys(overrideErrors).length > 0) {
          stateOverrideErrors[index] = overrideErrors;
        }
      });

      if (stateOverrideErrors.length > 0) {
        errors.stateOverrides = stateOverrideErrors;
      }
    }

    return errors;
  };

  const handleSubCompanyIdForVendor = (vendorAlias: string, currentSubCompanyId: string | undefined) => {
    const selectedVendor = directPayMaterialVendors?.materialVendors.find(
      (vendor: DirectPayMaterialVendor) => vendor.directPayMaterialVendorAlias === vendorAlias,
    );

    const vendorDoesNotHaveSubCompanies = !selectedVendor?.subCompanies?.length;
    if (vendorDoesNotHaveSubCompanies) {
      return '';
    }
    return currentSubCompanyId || '';
  };

  const transformVendorForBackend = <
    T extends { directPayMaterialVendorAlias: string; programName?: string; subCompanyId?: string },
  >(
    vendor: T,
  ): T => {
    const transformed = { ...vendor };

    if (transformed.directPayMaterialVendorAlias !== DirectPayMaterialVendorAliasOptions.ced) {
      transformed.programName = '';
    }

    transformed.subCompanyId = handleSubCompanyIdForVendor(
      transformed.directPayMaterialVendorAlias,
      transformed.subCompanyId,
    );

    return transformed;
  };

  const handleSubmit = useCallback(
    async (values: any, { setSubmitting }: any) => {
      try {
        const updates = { ...values };

        // Destructure stateOverrides and additionalOptInVendors
        const {
          stateOverrides: formStateOverrides,
          additionalOptInVendors: formOptInVendors,
          ...baseUpdates
        } = updates;

        if (baseUpdates.directPayMaterialVendorAlias !== DirectPayMaterialVendorAliasOptions.ced) {
          baseUpdates.programName = '';
        }

        baseUpdates.subCompanyId = handleSubCompanyIdForVendor(
          baseUpdates.directPayMaterialVendorAlias,
          baseUpdates.subCompanyId,
        );

        const backendStateOverrides = formStateOverrides.map((override: FormStateOverrideItem) => {
          const { states: formStates, ...vendorSettings } = override;
          return {
            ...transformVendorForBackend(vendorSettings),
            states: formStates.map((state) => state.value),
          };
        });

        const backendOptInVendors = (formOptInVendors || []).map(transformVendorForBackend);

        baseUpdates.stateOverrides = backendStateOverrides;
        baseUpdates.additionalOptInVendors = backendOptInVendors;
        baseUpdates.programType = programType;
        await updateDirectPaySettingsMutation({
          alias: organization.alias,
          directPaySettings: baseUpdates as OrganizationDirectPaySettingsV2,
        }).unwrap();
        toast.success('Settings submitted successfully');
      } catch (_err: any) {
        if (isErrorWithData(_err)) {
          const errorMessage = _err.data.message;
          toast.error(errorMessage);
        } else {
          console.error(_err);
          toast.error('Error submitting settings');
        }
      }
      setSubmitting(false);
    },
    [organization, programType, updateDirectPaySettingsMutation, directPayMaterialVendors],
  );

  const getOptInVendorSubCompanyPairs = (formValues: any): Set<string> =>
    new Set(
      (formValues.additionalOptInVendors || []).map(
        (o: FormOptInVendorItem) => `${o.directPayMaterialVendorAlias}:${o.subCompanyId}`,
      ),
    );

  const getFilteredVendorOptionsForOptIn = (
    allVendors: Array<{ label: string; value: string }>,
    formValues: any,
    currentIndex: number,
  ) => {
    const usedOptInPairs = new Set(
      (formValues.additionalOptInVendors || [])
        .filter((_: any, i: number) => i !== currentIndex)
        .map((o: FormOptInVendorItem) => `${o.directPayMaterialVendorAlias}:${o.subCompanyId}`),
    );
    const usedStatePairs = getUsedVendorSubCompanyPairs(formValues.stateOverrides);
    const currentVendor = formValues.additionalOptInVendors[currentIndex].directPayMaterialVendorAlias;

    return allVendors.filter((vendor) => {
      const isCurrentVendor = vendor.value === currentVendor;
      const isNotDefaultVendor = vendor.value !== formValues.directPayMaterialVendorAlias || isCurrentVendor;

      const subCompanyOptions = getSubCompanyOptions(vendor.value);
      let isNotExhausted: boolean;
      if (subCompanyOptions.length === 0) {
        const usedElsewhere =
          usedStatePairs.has(`${vendor.value}:`) ||
          (formValues.additionalOptInVendors || [])
            .filter((_: any, i: number) => i !== currentIndex)
            .some((o: FormOptInVendorItem) => o.directPayMaterialVendorAlias === vendor.value);
        isNotExhausted = !usedElsewhere || isCurrentVendor;
      } else {
        const hasAvailableSubCompany = subCompanyOptions.some(
          (sc) =>
            !usedOptInPairs.has(`${vendor.value}:${sc.value}`) && !usedStatePairs.has(`${vendor.value}:${sc.value}`),
        );
        isNotExhausted = hasAvailableSubCompany || isCurrentVendor;
      }

      return isNotDefaultVendor && isNotExhausted;
    });
  };

  const getFilteredSubCompanyOptionsForOptIn = (vendorAlias: string, formValues: any, currentIndex: number) => {
    const usedOptInPairs = new Set(
      (formValues.additionalOptInVendors || [])
        .filter((_: any, i: number) => i !== currentIndex)
        .map((o: FormOptInVendorItem) => `${o.directPayMaterialVendorAlias}:${o.subCompanyId}`),
    );
    const usedStatePairs = getUsedVendorSubCompanyPairs(formValues.stateOverrides);
    const currentSubCompanyId = formValues.additionalOptInVendors[currentIndex].subCompanyId;
    return getSubCompanyOptions(vendorAlias).filter(
      (sc) =>
        (!usedOptInPairs.has(`${vendorAlias}:${sc.value}`) && !usedStatePairs.has(`${vendorAlias}:${sc.value}`)) ||
        sc.value === currentSubCompanyId,
    );
  };

  const getFilteredVendorOptionsForOverride = (
    allVendors: Array<{ label: string; value: string }>,
    formValues: any,
    currentIndex: number,
  ) => {
    const usedStatePairs = getUsedVendorSubCompanyPairs(formValues.stateOverrides, currentIndex);
    const usedOptInPairs = getOptInVendorSubCompanyPairs(formValues);
    const currentVendor = formValues.stateOverrides[currentIndex].directPayMaterialVendorAlias;

    return allVendors.filter((vendor) => {
      const isCurrentVendor = vendor.value === currentVendor;

      const isNotDefaultVendor = vendor.value !== formValues.directPayMaterialVendorAlias || isCurrentVendor;

      const isUsedInOptIns = (formValues.additionalOptInVendors || []).some(
        (o: FormOptInVendorItem) => o.directPayMaterialVendorAlias === vendor.value,
      );

      const subCompanyOptions = getSubCompanyOptions(vendor.value);
      let isNotExhausted: boolean;
      if (subCompanyOptions.length === 0) {
        const isUsedInOtherOverride = formValues.stateOverrides
          .filter((_: any, i: number) => i !== currentIndex)
          .some((o: FormStateOverrideItem) => o.directPayMaterialVendorAlias === vendor.value);
        isNotExhausted = (!isUsedInOtherOverride && !isUsedInOptIns) || isCurrentVendor;
      } else {
        // Pair-level: a multi-sub-company vendor (e.g. Sonepar) stays selectable as long as any
        // sub-company is unused across state overrides AND opt-ins — the backend enforces
        // uniqueness per vendor+subCompany, not per alias, so alias-level exclusion is wrong here.
        const hasAvailableSubCompany = subCompanyOptions.some(
          (sc) =>
            !usedStatePairs.has(`${vendor.value}:${sc.value}`) && !usedOptInPairs.has(`${vendor.value}:${sc.value}`),
        );
        isNotExhausted = hasAvailableSubCompany || isCurrentVendor;
      }

      return isNotDefaultVendor && isNotExhausted;
    });
  };

  const getFilteredSubCompanyOptionsForOverride = (vendorAlias: string, formValues: any, currentIndex: number) => {
    const usedStatePairs = getUsedVendorSubCompanyPairs(formValues.stateOverrides, currentIndex);
    const usedOptInPairs = getOptInVendorSubCompanyPairs(formValues);
    const currentSubCompanyId = formValues.stateOverrides[currentIndex].subCompanyId;
    return getSubCompanyOptions(vendorAlias).filter(
      (sc) =>
        (!usedStatePairs.has(`${vendorAlias}:${sc.value}`) && !usedOptInPairs.has(`${vendorAlias}:${sc.value}`)) ||
        sc.value === currentSubCompanyId,
    );
  };

  const getFilteredStateOptionsForOverride = (
    allStateOptions: Array<{ label: string; value: string }>,
    formValues: any,
    currentIndex: number,
  ) => {
    return allStateOptions.filter((stateOption) => {
      const selectedStatesInOtherOverrides = formValues.stateOverrides
        .filter((_: any, i: number) => i !== currentIndex) // Exclude current vendor override
        .flatMap((o: FormStateOverrideItem) => o.states.map((state: { value: string }) => state.value));

      const currentOverrideStates = formValues.stateOverrides[currentIndex].states.map(
        (state: { value: string }) => state.value,
      );
      return (
        !selectedStatesInOtherOverrides.includes(stateOption.value) ||
        currentOverrideStates.includes(stateOption.value)
      );
    });
  };

  const programTypesOptions = useMemo(() => {
    return (
      organization?.programTypes?.map((pt) => ({
        label: ProgramTypeLabels[pt],
        value: pt,
      })) || []
    );
  }, [organization]);

  return (
    <Box gap="lg">
      {organization?.programTypes?.length && organization?.programTypes?.length > 1 && (
        <Box maxWidth="2xl">
          <SelectInputNative
            id="required"
            label="Program Type"
            value={programType}
            onChange={(event) => setProgramType(event.target.value)}
            options={programTypesOptions}
            menuPortalTarget={document.body}
          />
        </Box>
      )}
      <Box
        childGap={{
          base: 'lg',
          desktop: '2xl',
          hd: '3xl',
        }}
        padding={{
          base: 'lg',
          desktop: '0',
        }}
        width="50%"
        display="block"
      >
        <Box childGap="2xs">
          <Box as="h2" fontSize="md" fontWeight="medium">
            Direct Pay Settings
          </Box>
          {directPayData?.inherited && (
            <Box direction="row" alignItems="baseline" justifyContent="space-between" padding="xs 0">
              <Badge variant="info" message="Inherited from Parent Organization" />
            </Box>
          )}
        </Box>
        {!isLoading || !isFetching || !areDirectPayMaterialVendorsLoading ? (
          <Formik
            validationSchema={directPaySchema}
            initialValues={
              data
                ? getInitialValues(directPayData as OrganizationDirectPaySettingsV2)
                : {
                    directPayDefaultEnabled: false,
                    directPayMaterialVendorAlias: '',
                    directPayInstallerIdentifier: '',
                    programName: '',
                    stateOverrides: [],
                    additionalOptInVendors: [],
                  }
            }
            validate={handleValidation}
            validateOnChange={false}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, setFieldValue, values, dirty }) => (
              <EnhancedFormikForm>
                {dirty && !isSubmitting && (
                  <Box
                    style={{
                      position: 'fixed',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      zIndex: 100,
                      backgroundColor: '#fefce8',
                      borderTop: '2px solid #fde047',
                      color: '#854d0e',
                      fontSize: '14px',
                      padding: '12px 24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    role="status"
                  >
                    There are unsaved changes
                  </Box>
                )}
                <FormSection
                  title="Default Settings"
                  description="When enabled, accounts that reach NTP in states without a specific vendor override are automatically enrolled with this vendor."
                >
                  <Box childGap={{ base: 'lg', desktop: 'xl' }} style={{ whiteSpace: 'pre-wrap' }}>
                    {values.directPayDefaultEnabled ? (
                      <Box
                        padding="md"
                        borderWidth="xs"
                        borderColor="separator"
                        style={{ borderRadius: '16px' }}
                        childGap="md"
                      >
                        <Box direction="row" justifyContent="space-between" alignItems="center">
                          <Box as="h4" fontSize="sm" fontWeight="medium">
                            Global Default
                          </Box>
                          <Button
                            iconPrefix="c-remove"
                            variant="tertiary"
                            tone="neutral"
                            onClick={() => {
                              setFieldValue('directPayDefaultEnabled', false);
                              setFieldValue('directPayMaterialVendorAlias', '');
                              setFieldValue('directPayInstallerIdentifier', '');
                              setFieldValue('subCompanyId', '');
                              setFieldValue('programName', '');
                            }}
                            aria-label="Remove global default"
                            size="sm"
                            isDisabled={isSubmitting || isLoading || isFetching}
                          />
                        </Box>

                        <Field
                          type="text"
                          label="Direct Pay Material Vendor"
                          name="directPayMaterialVendorAlias"
                          id="directPayMaterialVendorAlias"
                          options={formattedDirectPayMaterialVendors.filter((vendor) => {
                            const selectedOverrideVendors = values.stateOverrides.map(
                              (option: FormStateOverrideItem) => option.directPayMaterialVendorAlias,
                            );
                            const selectedOptInVendors = (values.additionalOptInVendors || []).map(
                              (option: FormOptInVendorItem) => option.directPayMaterialVendorAlias,
                            );
                            return (
                              (!selectedOverrideVendors.includes(vendor.value) &&
                                !selectedOptInVendors.includes(vendor.value)) ||
                              vendor.value === values.directPayMaterialVendorAlias
                            );
                          })}
                          component={FormikSelectInputNative}
                          onChange={(event: any) =>
                            setFieldValue('directPayMaterialVendorAlias', event.target.value)
                          }
                          autoComplete="off"
                          isDisabled={isSubmitting || isLoading || isFetching}
                        />

                        {values.directPayMaterialVendorAlias &&
                          getSubCompanyOptions(values.directPayMaterialVendorAlias).length > 0 && (
                            <Field
                              type="text"
                              label="Sub-Company"
                              name="subCompanyId"
                              id="subCompanyId"
                              options={getSubCompanyOptions(values.directPayMaterialVendorAlias)}
                              component={FormikSelectInputNative}
                              onChange={(event: any) => {
                                setFieldValue('subCompanyId', event.target.value);
                              }}
                              autoComplete="off"
                              isDisabled={isSubmitting || isLoading || isFetching}
                            />
                          )}

                        <Field
                          type="text"
                          label="Direct Pay EPC Identifier"
                          name="directPayInstallerIdentifier"
                          id="directPayInstallerIdentifier"
                          component={FormikTextInput}
                          autoComplete="off"
                          isDisabled={isSubmitting || isLoading || isFetching}
                        />

                        {values.directPayMaterialVendorAlias === DirectPayMaterialVendorAliasOptions.ced && (
                          <Field
                            type="text"
                            label="Greentech Program Name"
                            name="programName"
                            id="programName"
                            options={programNameOptions}
                            component={FormikSelectInputNative}
                            onChange={(event: any) => setFieldValue('programName', event.target.value)}
                            autoComplete="off"
                            isDisabled={isSubmitting || isLoading || isFetching}
                          />
                        )}
                      </Box>
                    ) : (
                      <Box>
                        <Button
                          variant="secondary"
                          size="sm"
                          type="button"
                          onClick={() => setFieldValue('directPayDefaultEnabled', true)}
                          isDisabled={isSubmitting || isLoading || isFetching}
                        >
                          Add global default
                        </Button>
                      </Box>
                    )}
                  </Box>
                </FormSection>

                <FormSection
                  title="State-Specific Vendors"
                  description="Accounts in these states use the configured vendor at NTP, regardless of the global default."
                >
                  <Box childGap={{ base: 'lg', desktop: 'xl' }} style={{ whiteSpace: 'pre-wrap' }}>
                    <FieldArray
                      name="stateOverrides"
                      render={(arrayHelpers) => (
                        <Box childGap="lg">
                          {values.stateOverrides && values.stateOverrides.length > 0
                            ? values.stateOverrides.map((_override: FormStateOverrideItem, index: number) => (
                                <Box
                                  key={`stateOverrides-${index}`}
                                  padding="md"
                                  borderWidth="xs"
                                  borderColor="separator"
                                  style={{
                                    borderRadius: '16px',
                                  }}
                                  childGap="md"
                                >
                                  <Box direction="row" justifyContent="space-between" alignItems="center">
                                    <Box as="h4" fontSize="sm" fontWeight="medium">
                                      Vendor Override {index + 1}
                                    </Box>
                                    <Button
                                      iconPrefix="c-remove"
                                      variant="tertiary"
                                      tone="neutral"
                                      onClick={() => arrayHelpers.remove(index)}
                                      aria-label="Remove vendor override"
                                      size="sm"
                                    />
                                  </Box>
                                  <Field
                                    type="text"
                                    label="Direct Pay Material Vendor"
                                    name={`stateOverrides.${index}.directPayMaterialVendorAlias`}
                                    id={`stateOverrides.${index}.directPayMaterialVendorAlias`}
                                    options={getFilteredVendorOptionsForOverride(
                                      formattedDirectPayMaterialVendors,
                                      values,
                                      index,
                                    )}
                                    component={FormikSelectInputNative}
                                    autoComplete="off"
                                    isDisabled={isSubmitting || isLoading || isFetching}
                                  />

                                  {values.stateOverrides[index].directPayMaterialVendorAlias &&
                                    getFilteredSubCompanyOptionsForOverride(
                                      values.stateOverrides[index].directPayMaterialVendorAlias,
                                      values,
                                      index,
                                    ).length > 0 && (
                                      <Field
                                        type="text"
                                        label="Sub-Company"
                                        name={`stateOverrides.${index}.subCompanyId`}
                                        id={`stateOverrides.${index}.subCompanyId`}
                                        options={getFilteredSubCompanyOptionsForOverride(
                                          values.stateOverrides[index].directPayMaterialVendorAlias,
                                          values,
                                          index,
                                        )}
                                        component={FormikSelectInputNative}
                                        onChange={(event: any) => {
                                          setFieldValue(`stateOverrides.${index}.subCompanyId`, event.target.value);
                                        }}
                                        autoComplete="off"
                                        isDisabled={isSubmitting || isLoading || isFetching}
                                      />
                                    )}

                                  <Field
                                    type="text"
                                    label="Direct Pay EPC Identifier"
                                    name={`stateOverrides.${index}.directPayInstallerIdentifier`}
                                    id={`stateOverrides.${index}.directPayInstallerIdentifier`}
                                    component={FormikTextInput}
                                    autoComplete="off"
                                    isDisabled={isSubmitting || isLoading || isFetching}
                                  />

                                  {values.stateOverrides[index].directPayMaterialVendorAlias ===
                                    DirectPayMaterialVendorAliasOptions.ced && (
                                    <Field
                                      type="text"
                                      label="Greentech Program Name"
                                      name={`stateOverrides.${index}.programName`}
                                      id={`stateOverrides.${index}.programName`}
                                      options={programNameOptions}
                                      component={FormikSelectInputNative}
                                      autoComplete="off"
                                      isDisabled={isSubmitting || isLoading || isFetching}
                                    />
                                  )}

                                  <Field
                                    name={`stateOverrides.${index}.states`}
                                    id={`stateOverrides.${index}.states`}
                                    label="States"
                                    component={FormikSelectInput}
                                    isMulti
                                    options={getFilteredStateOptionsForOverride(stateOptions, values, index)}
                                    isDisabled={isSubmitting || isLoading || isFetching}
                                    placeholder="Select states for this vendor"
                                  />
                                </Box>
                              ))
                            : null}

                          <Box>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() =>
                                arrayHelpers.push({
                                  directPayMaterialVendorAlias: '',
                                  directPayInstallerIdentifier: '',
                                  programName: '',
                                  subCompanyId: '',
                                  states: [],
                                })
                              }
                              isDisabled={isSubmitting || isLoading || isFetching}
                            >
                              Add Vendor Override
                            </Button>
                          </Box>
                        </Box>
                      )}
                    />
                  </Box>
                </FormSection>

                <FormSection
                  title="Additional Available Opt-in Vendors"
                  description="Add additional vendors that can be used when manually opting in an account. Cannot include vendors already selected in above sections."
                >
                  <Box childGap={{ base: 'lg', desktop: 'xl' }} style={{ whiteSpace: 'pre-wrap' }}>
                    <FieldArray
                      name="additionalOptInVendors"
                      render={(arrayHelpers) => (
                        <Box childGap="lg">
                          {values.additionalOptInVendors && values.additionalOptInVendors.length > 0
                            ? values.additionalOptInVendors.map((_vendor: FormOptInVendorItem, index: number) => (
                                <Box
                                  key={`additionalOptInVendors-${index}`}
                                  padding="md"
                                  borderWidth="xs"
                                  borderColor="separator"
                                  style={{
                                    borderRadius: '16px',
                                  }}
                                  childGap="md"
                                >
                                  <Box direction="row" justifyContent="space-between" alignItems="center">
                                    <Box as="h4" fontSize="sm" fontWeight="medium">
                                      Opt-in Vendor {index + 1}
                                    </Box>
                                    <Button
                                      iconPrefix="c-remove"
                                      variant="tertiary"
                                      tone="neutral"
                                      onClick={() => arrayHelpers.remove(index)}
                                      aria-label="Remove opt-in vendor"
                                      size="sm"
                                    />
                                  </Box>
                                  <Field
                                    type="text"
                                    label="Direct Pay Material Vendor"
                                    name={`additionalOptInVendors.${index}.directPayMaterialVendorAlias`}
                                    id={`additionalOptInVendors.${index}.directPayMaterialVendorAlias`}
                                    options={getFilteredVendorOptionsForOptIn(
                                      formattedDirectPayMaterialVendors,
                                      values,
                                      index,
                                    )}
                                    component={FormikSelectInputNative}
                                    autoComplete="off"
                                    isDisabled={isSubmitting || isLoading || isFetching}
                                  />

                                  {values.additionalOptInVendors[index].directPayMaterialVendorAlias &&
                                    getFilteredSubCompanyOptionsForOptIn(
                                      values.additionalOptInVendors[index].directPayMaterialVendorAlias,
                                      values,
                                      index,
                                    ).length > 0 && (
                                      <Field
                                        type="text"
                                        label="Sub-Company"
                                        name={`additionalOptInVendors.${index}.subCompanyId`}
                                        id={`additionalOptInVendors.${index}.subCompanyId`}
                                        options={getFilteredSubCompanyOptionsForOptIn(
                                          values.additionalOptInVendors[index].directPayMaterialVendorAlias,
                                          values,
                                          index,
                                        )}
                                        component={FormikSelectInputNative}
                                        onChange={(event: any) => {
                                          setFieldValue(
                                            `additionalOptInVendors.${index}.subCompanyId`,
                                            event.target.value,
                                          );
                                        }}
                                        autoComplete="off"
                                        isDisabled={isSubmitting || isLoading || isFetching}
                                      />
                                    )}

                                  <Field
                                    type="text"
                                    label="Direct Pay EPC Identifier"
                                    name={`additionalOptInVendors.${index}.directPayInstallerIdentifier`}
                                    id={`additionalOptInVendors.${index}.directPayInstallerIdentifier`}
                                    component={FormikTextInput}
                                    autoComplete="off"
                                    isDisabled={isSubmitting || isLoading || isFetching}
                                  />

                                  {values.additionalOptInVendors[index].directPayMaterialVendorAlias ===
                                    DirectPayMaterialVendorAliasOptions.ced && (
                                    <Field
                                      type="text"
                                      label="Greentech Program Name"
                                      name={`additionalOptInVendors.${index}.programName`}
                                      id={`additionalOptInVendors.${index}.programName`}
                                      options={programNameOptions}
                                      component={FormikSelectInputNative}
                                      autoComplete="off"
                                      isDisabled={isSubmitting || isLoading || isFetching}
                                    />
                                  )}
                                </Box>
                              ))
                            : null}

                          <Box>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() =>
                                arrayHelpers.push({
                                  directPayMaterialVendorAlias: '',
                                  directPayInstallerIdentifier: '',
                                  programName: '',
                                  subCompanyId: '',
                                })
                              }
                              isDisabled={isSubmitting || isLoading || isFetching}
                            >
                              Add Opt-in Vendor
                            </Button>
                          </Box>
                        </Box>
                      )}
                    />
                  </Box>
                </FormSection>

                <Box
                  direction={{
                    base: 'column',
                    tablet: 'row',
                  }}
                  alignItems={{
                    base: 'stretch',
                    tablet: 'flex-start',
                  }}
                  justifyContent={{
                    tablet: 'flex-start',
                  }}
                  childGap="sm"
                  style={{ flexShrink: 0 }}
                  padding="lg 0 0 0"
                >
                  <Button
                    as="a"
                    navigate={() => navigate(`/settings/organizations/${organization.alias}`)}
                    variant="secondary"
                    tone="neutral"
                    size="md"
                    isDisabled={isLoading || isFetching}
                    isLoading={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    type="submit"
                    isDisabled={isLoading || isFetching}
                    isLoading={isSubmitting}
                  >
                    Save
                  </Button>
                </Box>
              </EnhancedFormikForm>
            )}
          </Formik>
        ) : (
          <Spinner size="lg" />
        )}
      </Box>
    </Box>
  );
};

export default DirectPay;
